import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { X, Save } from "lucide-react";
import { CircularProgress } from "@mui/material";

const timeSchedules = [
  { value: "15 Mins", label: "15 min" },
  { value: "30 Mins", label: "30 min" },
  { value: "45 Mins", label: "45 min" },
  { value: "1 hr", label: "1 hr" },
];

const apiUrl = process.env.REACT_APP_API_URL;

export default function AddEndpointGroupForm({
  close,
  refresh,
  setRefresh,
  error,
  setError,
}) {
  const [groupName, setGroupName] = useState("");
  const [timeSchedule, setTimeSchedule] = useState("15 Mins");
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accessToken = useSelector((state) => state.accessToken.value);

  useEffect(() => {
    const getEndpoints = async () => {
      const query = `
        query MyQuery {
          getAvailableEndpoints(GroupName:"default"){
            EndpointName
            id
          }
        }
      `;
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (response.data.data && response.data.data.getAvailableEndpoints) {
        setAvailableEndpoints(response.data.data.getAvailableEndpoints);
      }
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError("Unauthorized");
    };
    getEndpoints();
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const changeSchedule = async () => {
      const query = `
        mutation MyMutation($groupName: String!, $timeInterval: String!) {
          updateSchedule(groupName: $groupName, timeInterval: $timeInterval, task: "add")
        }
      `;
      const variables = {
        groupName: groupName,
        timeInterval: timeSchedule,
      };
      await axios
        .post(
          apiUrl,
          { query, variables },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          if (
            response.data.errors &&
            response.data.errors[0].errorType == "Unauthorized"
          )
            setError(
              "Edit access is not granted to your account for this resource."
            );
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const addEndpoints = async (id) => {
      const query = `
        mutation MyMutation($GroupName: String!, $id: String!) {
          updateEndpointGroup(GroupName: $GroupName, id: $id)
        }
      `;
      const variables = {
        id: id,
        GroupName: groupName,
      };
      await axios
        .post(
          apiUrl,
          { query, variables },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          if (
            response.data.errors &&
            response.data.errors[0].errorType == "Unauthorized"
          )
            setError(
              "Edit access is not granted to your account for this resource."
            );
        })
        .catch((error) => {
          console.log(error);
        });
    };

    await changeSchedule();
    await Promise.all(
      selectedEndpoints.map((endpoint) => addEndpoints(endpoint.id))
    );
    setRefresh(!refresh);
    setIsSubmitting(false);
    close();
  };

  const handleEndpointToggle = (endpoint) => {
    setSelectedEndpoints(prev => {
      const isSelected = prev.some(e => e.id === endpoint.id);
      if (isSelected) {
        return prev.filter(e => e.id !== endpoint.id);
      } else {
        return [...prev, endpoint];
      }
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-xl overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="p-6 space-y-6 overflow-y-auto">
          <h2 className="text-xl font-semibold text-[#003366]">
              Create Endpoint Group
            </h2> 
            {/* Group Name Input */}
            <div className="space-y-2">
              <label
                htmlFor="groupName"
                className="block text-lg font-bold"
              >
                Group Name
              </label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                className="w-full px-3 py-2 border  text-[#003366] border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter group name"
              />
            </div>

            {/* Time Schedule Radio Buttons */}
            <div className="space-y-3">
              <label className="block text-lg font-bold ">
                Time Schedule
              </label>
              <div className="grid grid-cols-2 gap-3">
                {timeSchedules.map((schedule) => (
                  <div key={schedule.value} className="flex items-center">
                    <input
                      id={`schedule-${schedule.value}`}
                      type="radio"
                      name="timeSchedule"
                      value={schedule.value}
                      checked={timeSchedule === schedule.value}
                      onChange={() => setTimeSchedule(schedule.value)}
                      className="h-4 w-4 text-[#003366] focus:ring-[#003366] border-gray-300"
                    />
                    <label
                      htmlFor={`schedule-${schedule.value}`}
                      className="ml-3 block text-base font-medium text-[#003366]"
                    >
                      {schedule.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Endpoints Checkboxes */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-lg font-bold ">
                  Select Endpoints
                </label>
                <div className="text-xs text-gray-500">
                  {selectedEndpoints.length} selected
                </div>
              </div>
              <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto">
                {availableEndpoints.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">No endpoints available</p>
                ) : (
                  availableEndpoints.map((endpoint) => (
                    <div key={endpoint.id} className="flex items-center py-2 px-1 hover:bg-gray-50">
                      <input
                        id={`endpoint-${endpoint.id}`}
                        type="checkbox"
                        checked={selectedEndpoints.some(e => e.id === endpoint.id)}
                        onChange={() => handleEndpointToggle(endpoint)}
                        className="h-4 w-4 text-[#003366] focus:ring-[#003366] border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`endpoint-${endpoint.id}`}
                        className="ml-3 block text-base font-medium text-[#003366] cursor-pointer"
                      >
                        {endpoint.EndpointName}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
            <button
              type="button"
              onClick={close}
              className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80 "
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save size={16} />
              )}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}